/**
 * The player's account, from the server's HTTP API. The device keeps one session token in
 * localStorage; the first visit quietly makes a guest account (no form). Another device signs in
 * to the same account with a one-time code made here. In Telegram the launch data signs in instead:
 * the Telegram user's own account, made on the first launch, again with no form.
 */

export type NickError = 'invalid' | 'offensive' | 'reserved' | 'taken';
export interface ShopItem { sku: string; title: string; description: string; stars: number; kind: string }

/** The recognition a supporter's profile shows (highest first). */
export function supporterTitle(owned: readonly string[]): string | null {
  return owned.includes('patron') ? 'Меценат гри' : owned.includes('supporter_gold') ? 'Щедрий покровитель' : owned.includes('supporter') ? 'Покровитель гри' : null;
}

/** The record boards (GET /leaderboard): this Kyiv week's or all time's. */
export type BoardPeriod = 'week' | 'all';
export interface BoardEntry { rank: number; nick: string | null; kills: number; sign: string | null; you?: true }
export interface Board {
  period: BoardPeriod;
  /** The current week: its Monday and when it ends. */
  week: { start: string; endsAt: string };
  top: BoardEntry[];
  /** Signed in only: rank null without a server match in the period; prevRank (week): last week's place. */
  me: { rank: number | null; kills: number; prevRank?: number | null } | null;
}
export interface Progress { matches: number; ticks: number; kills: number; bossKills: number; bestKills: number }
export interface Profile {
  account: { id: number; nick: string | null; source: string; createdAt: string };
  progress: Progress;
  /** Linked sign-in providers ('telegram'). */
  identities: string[];
  /** Owned items (shop recognition badges: supporter, supporter_gold, patron). */
  collection?: { item: string; source: string; acquiredAt: string }[];
  matches: { code: string; mode: string; endedAt: string; ticks: number; seat: number; kills: number; bossKills: number; leaks: number }[];
}

const KEY = 'orb.account';
const load = () => { try { return localStorage.getItem(KEY); } catch { return null; } };
const keep = (token: string | null) => { try { if (token) localStorage.setItem(KEY, token); else localStorage.removeItem(KEY); } catch { /* private mode */ } };

/** The API lives next to the game socket: ws(s)://host/ws → http(s)://host. */
export const apiBase = (socketUrl: string) => socketUrl.replace(/^ws/, 'http').replace(/\/ws\/?$/, '');

export class AccountError extends Error {
  constructor(readonly status: number, readonly code: string) { super(code); }
}

/** Where the sign-in stands (session.ts turns it into what the player may do). */
export type SignIn = 'anonymous' | 'signing' | 'signed' | 'offline' | 'relaunch';

export class Account {
  token: string | null = load();
  profile: Profile | null = null;
  /** The Telegram launch's `start_param` (`r_<room>`, `c_<campaign>`), once signed in through it. */
  startParam: string | null = null;
  /** `https://t.me/<bot>/<app>` from the server (Telegram sign-in): invites open the Mini App. */
  appUrl: string | null = null;
  /**
   * anonymous: no session yet (the web before it needs one) · signing · signed · offline: the server
   * can't be reached · relaunch: Telegram's launch data expired and there is no session to fall back on.
   */
  state: SignIn = 'anonymous';
  private readonly base: string;
  /** Telegram's signed launch data; exchanged for a session once per page load. */
  private readonly initData: string | null;
  private telegram: Promise<'ok' | 'refused' | 'disabled' | 'offline'> | null = null;
  private readonly listeners = new Set<() => void>();
  constructor(socketUrl: string, initData: string | null = null) { this.base = apiBase(socketUrl); this.initData = initData || null; }

  /** Inside Telegram: the account is the Telegram user's (never a guest of this WebView). */
  get inTelegram() { return !!this.initData; }

  /** Called whenever the profile or the sign-in state changes; returns the unsubscribe. */
  subscribe(fn: () => void): () => void { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  private emit() { for (const fn of [...this.listeners]) fn(); }
  private setState(s: SignIn) { if (s !== this.state) { this.state = s; this.emit(); } }
  private setProfile(p: Profile) { this.profile = p; this.state = 'signed'; this.emit(); return p; }

  /** One HTTP request; AccountError on a non-2xx answer, the fetch's own error when unreachable. */
  private async request<T>(method: string, path: string, body?: unknown, token = this.token): Promise<T> {
    const res = await fetch(this.base + path, {
      method,
      headers: { ...(token ? { authorization: `Bearer ${token}` } : {}), ...(body ? { 'content-type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new AccountError(res.status, (json as { error?: string }).error ?? 'http_' + res.status);
    return json as T;
  }

  /**
   * A request on the player's behalf. A session that ended meanwhile (401: a month away, signed out
   * elsewhere) is renewed once — through Telegram again inside it — and the request repeated.
   */
  private async call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = this.token;
    try { return await this.request<T>(method, path, body, token); } catch (e) {
      if (!(e instanceof AccountError) || e.status !== 401 || !token) throw e;
      if (this.token === token) { this.token = null; keep(null); this.telegram = null; }
      await this.signIn();
      return this.request<T>(method, path, body);
    }
  }

  /**
   * Signed in after this: the stored session if it still works, otherwise a new guest — on the web.
   * Inside Telegram the launch data signs in, and no guest is ever made there (the Telegram user's
   * progress must not split): expired launch data with no session left is `relaunch`. Throws when the
   * server can't be reached (offline solo still plays) or on `relaunch`.
   */
  private signing: Promise<Profile> | null = null;
  /** One sign-in at a time: callers arriving meanwhile share it (no second guest account). */
  signIn(): Promise<Profile> { return (this.signing ??= this.doSignIn().finally(() => { this.signing = null; })); }

  private async doSignIn(): Promise<Profile> {
    this.setState('signing');
    try {
      let refused = false;
      if (this.initData) refused = await (this.telegram ??= this.signInTelegram().then((r) => { if (r === 'offline') this.telegram = null; return r; })) === 'refused';
      if (this.token) {
        try { return this.setProfile(await this.request<Profile>('GET', '/me')); } catch (e) {
          if (!(e instanceof AccountError) || e.status !== 401) throw e;
          // expired after a month away, or signed out elsewhere
          this.token = null; keep(null);
        }
      }
      if (refused) throw new AccountError(401, 'relaunch');
      const issued = await this.request<{ token: string }>('POST', '/auth/guest', undefined, null);
      this.token = issued.token; keep(issued.token);
      return this.setProfile(await this.request<Profile>('GET', '/me'));
    } catch (e) {
      this.setState(e instanceof AccountError ? (e.code === 'relaunch' ? 'relaunch' : this.profile ? 'signed' : 'anonymous') : 'offline');
      throw e;
    }
  }

  refresh() { return this.signIn(); }

  /**
   * Every launch from Telegram: the launch data for a fresh session (the old token goes with the
   * request — a guest without Telegram keeps its progress by taking this identity — and then ends).
   * `refused`: the launch data is a day old (the app left open) or invalid — the stored session may
   * still carry on, but a guest must not be made instead. A server without Telegram (`telegram_disabled`,
   * development) signs in as on the web.
   */
  private async signInTelegram(): Promise<'ok' | 'refused' | 'disabled' | 'offline'> {
    try {
      const issued = await this.request<{ token: string; startParam: string | null; appUrl?: string | null }>('POST', '/auth/telegram', { initData: this.initData });
      this.token = issued.token; keep(issued.token);
      this.startParam = issued.startParam;
      this.appUrl = issued.appUrl ?? null;
      return 'ok';
    } catch (e) {
      if (!(e instanceof AccountError)) return 'offline'; // tried again next time
      return e.code === 'telegram_disabled' ? 'disabled' : 'refused';
    }
  }

  /** The profile again from the server (e.g. the nick was set from another device meanwhile). */
  /** How many players are online now (no account needed); null when the server can't be reached. */
  async online(): Promise<number | null> {
    try { return (await this.request<{ online: number }>('GET', '/stats/online', undefined, null)).online; } catch { return null; }
  }

  /**
   * A record board: anyone may look (no guest is made for it); with a session it also says where
   * this account stands. The session isn't renewed here — a stale one just reads as no place.
   */
  leaderboard(period: BoardPeriod) { return this.request<Board>('GET', `/leaderboard?period=${period}`); }

  async reload(): Promise<Profile> { return this.setProfile(await this.call<Profile>('GET', '/me')); }

  /** Whether this account still has to choose its nick (chosen once, on the first visit). */
  get needsNick() { return !!this.profile && this.profile.account.nick === null; }

  /** A free nick to start from: the Telegram username or first name, or a fallback. */
  async suggestNick(): Promise<string> { return (await this.call<{ nick: string }>('GET', '/me/nick/suggest')).nick; }

  /** Is this nick valid and free? `error`: invalid | offensive | reserved | taken. */
  checkNick(nick: string) {
    return this.call<{ ok: true; nick: string } | { ok: false; error: NickError }>('GET', `/me/nick/check?nick=${encodeURIComponent(nick)}`);
  }

  /** Sets the nick (once). Throws AccountError with invalid / offensive / reserved / taken / already_set. */
  async setNick(nick: string): Promise<string> {
    const r = await this.call<{ account: Profile['account'] }>('POST', '/me/nick', { nick });
    if (this.profile) this.setProfile({ ...this.profile, account: r.account });
    return r.account.nick!;
  }

  /** The Stars shop: what's for sale, what this account owns, whether it can buy here. */
  shop() { return this.call<{ items: ShopItem[]; owned: string[]; canBuy: boolean }>('GET', '/shop/catalog'); }
  /** A pending order and its Stars invoice link. */
  invoice(sku: string) { return this.call<{ order: number; link: string }>('POST', '/shop/invoice', { sku }); }
  order(id: number) { return this.call<{ id: number; sku: string; status: string }>('GET', `/shop/orders/${id}`); }

  /** A code for another device, valid 10 minutes, once. */
  linkCode() { return this.call<{ code: string; expiresAt: string }>('POST', '/auth/link'); }

  /** Signs this device in to the account a code came from (this device's own guest is left). */
  async claim(code: string): Promise<Profile> {
    const issued = await this.request<{ token: string }>('POST', '/auth/claim', { code }, this.token);
    this.token = issued.token; keep(issued.token);
    return this.setProfile(await this.request<Profile>('GET', '/me'));
  }
}
